import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import {
    Search,
    User,
    Building2,
    Briefcase,
    CheckSquare,
    Heart,
    X,
    Loader2,
    ArrowRight,
    Clock,
    Trash2,
    Send,
    FileSignature,
    Workflow
} from 'lucide-react';
import HighlightText from './HighlightText';

const ENTITY_CONFIG = {
    CONTACT: { icon: User, color: 'text-blue-600 bg-blue-50 border-blue-200 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800', label: 'Contact', route: '/crm/contacts', hint: '→ Contacts' },
    COMPANY: { icon: Building2, color: 'text-emerald-600 bg-emerald-50 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-300 dark:border-emerald-800', label: 'Company', route: '/crm/companies', hint: '→ Companies' },
    OPPORTUNITY: { icon: Briefcase, color: 'text-purple-600 bg-purple-50 border-purple-200 dark:bg-purple-900/40 dark:text-purple-300 dark:border-purple-800', label: 'Deal', route: '/opportunities', hint: '→ Pipeline' },
    TASK: { icon: CheckSquare, color: 'text-orange-600 bg-orange-50 border-orange-200 dark:bg-orange-900/40 dark:text-orange-300 dark:border-orange-800', label: 'Task', route: '/tasks', hint: '→ Tasks' },
    CS_PROFILE: { icon: Heart, color: 'text-teal-600 bg-teal-50 border-teal-200 dark:bg-teal-900/40 dark:text-teal-300 dark:border-teal-800', label: 'Success', route: '/success', hint: '→ Success' },
    CAMPAIGN: { icon: Send, color: 'text-pink-600 bg-pink-50 border-pink-200 dark:bg-pink-900/40 dark:text-pink-300 dark:border-pink-800', label: 'Campaign', route: '/campaigns', hint: '→ Campaigns' },
    TEMPLATE: { icon: FileSignature, color: 'text-amber-600 bg-amber-50 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800', label: 'Template', route: '/templates', hint: '→ Templates' },
    SEQUENCE: { icon: Workflow, color: 'text-indigo-600 bg-indigo-50 border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-300 dark:border-indigo-800', label: 'Sequence', route: '/sequences', hint: '→ Sequences' }
};

const TYPE_FILTERS = [
    { key: 'ALL', label: 'All' },
    { key: 'CONTACT', label: 'Contacts' },
    { key: 'COMPANY', label: 'Companies' },
    { key: 'OPPORTUNITY', label: 'Deals' },
    { key: 'TASK', label: 'Tasks' },
    { key: 'CS_PROFILE', label: 'Success' },
    { key: 'CAMPAIGN', label: 'Campaigns' },
    { key: 'TEMPLATE', label: 'Templates' },
    { key: 'SEQUENCE', label: 'Sequences' }
];

/**
 * GlobalSearchModal — Command-palette-style global search with deep-link
 * navigation and user-scoped recent access.
 */
export default function GlobalSearchModal({ isOpen, onClose }) {
    const navigate = useNavigate();
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [recentItems, setRecentItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(0);
    const [activeFilter, setActiveFilter] = useState('ALL');
    const inputRef = useRef(null);
    const debounceRef = useRef(null);

    // Focus input when modal opens; load recents; restore persisted query
    useEffect(() => {
        if (isOpen) {
            const saved = sessionStorage.getItem('mw_search_query');
            if (saved) setQuery(saved);
            setTimeout(() => inputRef.current?.focus(), 50);
            fetchRecent();
        } else {
            // Persist query on close if non-empty
            if (query.trim().length >= 2) {
                sessionStorage.setItem('mw_search_query', query);
            }
            setResults([]);
            setActiveIndex(0);
            setActiveFilter('ALL');
        }
    }, [isOpen, query]);

    const fetchRecent = async () => {
        try {
            const res = await api.get('/api/search/recent');
            setRecentItems(res.data.items || []);
        } catch (_) {
            setRecentItems([]);
        }
    };

    const recordRecent = async (item) => {
        try {
            await api.post('/api/search/recent', {
                entityType: item.entityType,
                entityId: item.id || item.entityId,
                title: item.title,
                subtitle: item.subtitle || '',
                badge: item.badge || '',
                route: item.route || ''
            });
        } catch (_) { /* best-effort */ }
    };

    const clearRecent = async () => {
        try {
            await api.delete('/api/search/recent');
            setRecentItems([]);
        } catch (_) { /* best-effort */ }
    };

    // Debounced search
    const doSearch = useCallback(async (q) => {
        if (!q || q.trim().length < 2) {
            setResults([]);
            return;
        }
        setLoading(true);
        try {
            const params = { q: q.trim() };
            if (activeFilter !== 'ALL') params.types = activeFilter;
            const res = await api.get('/api/search', { params });
            setResults(res.data.results || []);
            setActiveIndex(0);
        } catch (_) {
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, [activeFilter]);

    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => doSearch(query), 250);
        return () => clearTimeout(debounceRef.current);
    }, [query, doSearch]);

    // Deep-link navigation
    const navigateToEntity = (item) => {
        const entityType = item.entityType;
        const entityId = item.id || item.entityId;
        const config = ENTITY_CONFIG[entityType];
        if (!config) return;

        const basePath = config.route;
        navigate(`${basePath}?focus=${entityId}`);
    };

    // Navigate to result
    const handleSelect = (item) => {
        onClose();
        recordRecent(item);
        navigateToEntity(item);
    };

    // Compute per-type counts for badges
    const typeCounts = {};
    results.forEach(r => {
        typeCounts[r.entityType] = (typeCounts[r.entityType] || 0) + 1;
    });
    const totalCount = results.length;
    const filteredResults = activeFilter === 'ALL' ? results : results.filter(r => r.entityType === activeFilter);

    // Keyboard navigation + tab switching
    const displayItems = query.trim().length >= 2 ? filteredResults : [];
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            sessionStorage.removeItem('mw_search_query');
            setQuery('');
            onClose();
        } else if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(i => Math.min(i + 1, displayItems.length - 1));
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(i => Math.max(i - 1, 0));
        } else if (e.key === 'Enter' && displayItems[activeIndex]) {
            handleSelect(displayItems[activeIndex]);
        } else if (e.key === 'Tab') {
            e.preventDefault();
            const idx = TYPE_FILTERS.findIndex(f => f.key === activeFilter);
            const next = e.shiftKey
                ? (idx - 1 + TYPE_FILTERS.length) % TYPE_FILTERS.length
                : (idx + 1) % TYPE_FILTERS.length;
            setActiveFilter(TYPE_FILTERS[next].key);
            setActiveIndex(0);
        } else if (e.key >= '1' && e.key <= '6' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            const tabIdx = parseInt(e.key) - 1;
            if (TYPE_FILTERS[tabIdx]) {
                setActiveFilter(TYPE_FILTERS[tabIdx].key);
                setActiveIndex(0);
            }
        }
    };

    if (!isOpen) return null;

    const grouped = {};
    filteredResults.forEach(r => {
        if (!grouped[r.entityType]) grouped[r.entityType] = [];
        grouped[r.entityType].push(r);
    });

    const showRecents = query.trim().length < 2 && recentItems.length > 0;

    return (
        <>
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100] animate-in fade-in duration-150"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="fixed top-4 sm:top-[15%] left-1/2 -translate-x-1/2 w-[calc(100%-1.5rem)] sm:w-full max-w-xl z-[101] animate-in fade-in slide-in-from-top-4 duration-200">
                <div className="bg-background rounded-xl shadow-2xl border border-border overflow-hidden max-h-[calc(100vh-2rem)] sm:max-h-none flex flex-col">
                    {/* Search input */}
                    <div className="flex items-center px-4 border-b border-border">
                        <Search className="w-5 h-5 text-muted-foreground flex-shrink-0" />
                        <input
                            ref={inputRef}
                            type="text"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="Search contacts, deals, tasks, campaigns..."
                            className="flex-1 px-3 py-4 text-sm outline-none bg-transparent text-foreground placeholder:text-muted-foreground"
                            autoComplete="off"
                        />
                        {loading && <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />}
                        <button onClick={onClose} className="p-1 ml-2 rounded hover:bg-accent transition-colors">
                            <X className="w-4 h-4 text-muted-foreground" />
                        </button>
                    </div>

                    {/* Type filter tabs with count badges */}
                    <div className="flex items-center gap-1 px-4 py-2 border-b border-border bg-muted/30 overflow-x-auto">
                        {TYPE_FILTERS.map((f, idx) => {
                            const count = f.key === 'ALL' ? totalCount : (typeCounts[f.key] || 0);
                            const hasResults = query.trim().length >= 2 && count > 0;
                            return (
                                <button
                                    key={f.key}
                                    onClick={() => { setActiveFilter(f.key); setActiveIndex(0); }}
                                    className={`text-[10px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-md transition-colors whitespace-nowrap flex items-center gap-1.5 ${activeFilter === f.key
                                        ? 'bg-primary/10 text-primary border border-primary/20'
                                        : 'text-muted-foreground hover:bg-accent border border-transparent hover:text-foreground'
                                        }`}
                                >
                                    {f.label}
                                    {hasResults && (
                                        <span className={`text-[9px] min-w-[16px] h-4 flex items-center justify-center rounded-full font-bold ${activeFilter === f.key ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                                            {count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Results / Recents */}
                    <div className="max-h-[400px] overflow-y-auto">
                        {/* Recent items (empty query state) */}
                        {showRecents && (
                            <>
                                <div className="px-4 py-2 flex items-center justify-between bg-muted/40 border-b border-border">
                                    <span className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground flex items-center gap-1">
                                        <Clock className="w-3 h-3" /> Recent
                                    </span>
                                    <button onClick={clearRecent} className="text-[10px] text-muted-foreground hover:text-destructive flex items-center gap-1 transition-colors">
                                        <Trash2 className="w-3 h-3" /> Clear
                                    </button>
                                </div>
                                {recentItems.map(item => {
                                    const config = ENTITY_CONFIG[item.entityType] || {};
                                    const Icon = config.icon || Search;
                                    return (
                                        <button
                                            key={item._id || item.entityId}
                                            onClick={() => handleSelect({ ...item, id: item.entityId })}
                                            className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-accent transition-colors"
                                        >
                                            <div className={`flex-shrink-0 w-8 h-8 rounded-lg border flex items-center justify-center ${config.color}`}>
                                                <Icon className="w-4 h-4" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-foreground truncate">{item.title}</div>
                                                {item.subtitle && <div className="text-xs text-muted-foreground truncate">{item.subtitle}</div>}
                                            </div>
                                            {item.badge && (
                                                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${config.color}`}>
                                                    {item.badge}
                                                </span>
                                            )}
                                            <ArrowRight className="w-3.5 h-3.5 text-muted-foreground/30 flex-shrink-0" />
                                        </button>
                                    );
                                })}
                            </>
                        )}

                        {/* Empty query, no recents */}
                        {query.trim().length < 2 && !showRecents && (
                            <div className="px-4 py-6 text-center text-xs text-gray-400">
                                Type at least 2 characters to search
                            </div>
                        )}

                        {/* No results — contextual messaging */}
                        {query.trim().length >= 2 && filteredResults.length === 0 && !loading && (
                            <div className="px-4 py-12 text-center">
                                <Search className="w-8 h-8 text-muted-foreground/30 mx-auto mb-3" />
                                <div className="text-sm font-medium text-muted-foreground">
                                    No {activeFilter !== 'ALL' ? TYPE_FILTERS.find(f => f.key === activeFilter)?.label?.toLowerCase() : 'results'} found
                                </div>
                                <div className="text-xs text-muted-foreground/60 mt-1">
                                    for &ldquo;{query}&rdquo;{activeFilter !== 'ALL' && <> &middot; <button onClick={() => { setActiveFilter('ALL'); setActiveIndex(0); }} className="text-primary hover:underline">Search all types</button></>}
                                </div>
                            </div>
                        )}

                        {/* Grouped search results */}
                        {Object.entries(grouped).map(([type, items]) => {
                            const config = ENTITY_CONFIG[type] || {};
                            const Icon = config.icon || Search;
                            return (
                                <div key={type}>
                                    <div className="px-4 py-2 text-[10px] uppercase tracking-widest font-bold text-muted-foreground/60 bg-muted/40 border-b border-border">
                                        {config.label || type}
                                    </div>
                                    {items.map((item) => {
                                        const globalIdx = filteredResults.indexOf(item);
                                        return (
                                            <button
                                                key={item.id}
                                                onClick={() => handleSelect(item)}
                                                onMouseEnter={() => setActiveIndex(globalIdx)}
                                                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${globalIdx === activeIndex ? 'bg-primary/5 dark:bg-primary/10' : 'hover:bg-accent'
                                                    }`}
                                            >
                                                <div className={`flex-shrink-0 w-8 h-8 rounded-lg border flex items-center justify-center ${config.color}`}>
                                                    <Icon className="w-4 h-4" />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <div className="text-sm font-medium text-foreground truncate">
                                                        <HighlightText text={item.title} query={query} />
                                                    </div>
                                                    {item.subtitle && (
                                                        <div className="text-xs text-muted-foreground truncate">
                                                            <HighlightText text={item.subtitle} query={query} />
                                                            <span className="ml-1.5 text-muted-foreground/30">{config.hint}</span>
                                                        </div>
                                                    )}
                                                    {!item.subtitle && config.hint && (
                                                        <div className="text-xs text-muted-foreground/30 truncate">{config.hint}</div>
                                                    )}
                                                </div>
                                                {item.badge && (
                                                    <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${config.color}`}>
                                                        {item.badge}
                                                    </span>
                                                )}
                                                <ArrowRight className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" />
                                            </button>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>

                    {/* Footer hint */}
                    <div className="px-4 py-2 border-t border-border bg-muted/30 flex items-center justify-between text-[10px] text-muted-foreground/50">
                        <span>↑↓ navigate · ↵ select · tab switch type · esc close</span>
                        <span className="font-mono px-1.5 py-0.5 rounded bg-accent border border-border">⌘K</span>
                    </div>
                </div>
            </div>
        </>
    );
}
